import { AssignmentResultFilter } from '@app/pages/admin/assignment-result-count.util';
import { isOrchestrationProbeProcess } from '@app/pages/admin/assignment-start.util';
import {
  ASSIGNMENT_STATE_FILTERS,
  AssignmentStateFilter,
} from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';

const ASSIGNMENT_WORKER_GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Grouped process rows, or one row per run. */
export type AssignmentListMode = 'groups' | 'runs';

/** `runs` is the flat feed. Any other value, including a missing key, is the process groups. */
export function parseAssignmentListMode(value: string | null | undefined): AssignmentListMode {
  return value === 'runs' ? 'runs' : 'groups';
}

/** `monolith`, a lowercase enrollment-key id, or empty when the query is absent or invalid. */
export function parseAssignmentWorker(value: string | null | undefined): string {
  const raw = value?.trim() ?? '';
  if (!raw) {
    return '';
  }
  if (raw.toLowerCase() === 'monolith') {
    return 'monolith';
  }
  if (ASSIGNMENT_WORKER_GUID.test(raw)) {
    return raw.toLowerCase();
  }
  return '';
}

export interface AssignmentTableFilters {
  result: AssignmentResultFilter;
  state: AssignmentStateFilter;
  createdRange: Date[] | null;
  process?: string;
  worker?: string;
  /** Omitted means the grouped process list. */
  list?: AssignmentListMode;
}

type QueryParamReader = {
  get(name: string): string | null;
};

export function parseAssignmentTableFilterQuery(params: QueryParamReader): AssignmentTableFilters {
  const resultRaw = params.get('result');
  const result: AssignmentResultFilter = resultRaw === 'all' ? 'all' : 'positive';
  const stateRaw = params.get('state');
  const state: AssignmentStateFilter = ASSIGNMENT_STATE_FILTERS.some(id => id === stateRaw)
    ? stateRaw as AssignmentStateFilter
    : 'all';
  const from = parseLocalDay(params.get('createdFrom'));
  const to = parseLocalDay(params.get('createdTo'));
  let createdRange: Date[] | null = null;
  if (from && to) {
    createdRange = [from, to];
  } else if (from) {
    createdRange = [from];
  } else if (to) {
    createdRange = [to];
  }
  const processRaw = params.get('process')?.trim() ?? '';
  const process = isOrchestrationProbeProcess(processRaw) ? '' : processRaw;
  const worker = parseAssignmentWorker(params.get('worker'));
  const list = parseAssignmentListMode(params.get('list'));
  return { result, state, createdRange, process, worker, list };
}

export function serializeAssignmentTableFilterQuery(
  filters: AssignmentTableFilters,
): Record<string, string | null> {
  const dates = (filters.createdRange ?? []).filter((value): value is Date => value instanceof Date);
  const from = dates[0] ? formatLocalDay(dates[0]) : null;
  const to = dates[1] ? formatLocalDay(dates[1]) : from;
  return {
    result: filters.result === 'all' ? 'all' : null,
    state: filters.state === 'all' ? null : filters.state,
    createdFrom: from,
    createdTo: to,
    process: isOrchestrationProbeProcess(filters.process) || !filters.process?.trim()
      ? null
      : filters.process.trim(),
    worker: parseAssignmentWorker(filters.worker) || null,
    list: filters.list === 'runs' ? 'runs' : null,
    group: null,
    g: null,
  };
}

export function assignmentTableFiltersEqual(a: AssignmentTableFilters, b: AssignmentTableFilters): boolean {
  return a.result === b.result
    && a.state === b.state
    && (a.process ?? '') === (b.process ?? '')
    && parseAssignmentWorker(a.worker) === parseAssignmentWorker(b.worker)
    && (a.list ?? 'groups') === (b.list ?? 'groups')
    && createdRangeKey(a.createdRange) === createdRangeKey(b.createdRange);
}

/** True when the URL filters are new or differ from the last request. Compare `parsed`, not in-memory fields already written by the chip click. */
export function assignmentTableFiltersNeedReload(
  lastApplied: AssignmentTableFilters | null,
  parsed: AssignmentTableFilters,
): boolean {
  return lastApplied == null || !assignmentTableFiltersEqual(lastApplied, parsed);
}

function createdRangeKey(range: Date[] | null): string {
  const dates = (range ?? []).filter((value): value is Date => value instanceof Date);
  return dates.map(formatLocalDay).join('/');
}

function parseLocalDay(value: string | null): Date | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

function formatLocalDay(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
